import type { SelectChangeEvent, Theme } from '@mui/material'
import { Grid2 as Grid, Typography } from '@mui/material'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import type { VariableAllocationRow, VariableAllocationTable } from 'types/comp-allocations'
import { RowType } from 'types/comp-allocations'
import type { ExpandedTarget } from 'types/components'
import type { Period } from 'types/periods'
import { createLabelToIdMap, getOrderedLabels } from 'types/periods' // Adjust the import path as necessary
import AllocationTableDisplay from './AllocationTableDisplay/index' // Adjust the import path as necessary

interface TemplateComponentsProps {
  period: Period | null
  allocationTables: VariableAllocationTable[]
  targets: ExpandedTarget[]
  organizationLabels: Record<string, string>
  periodLabels: Record<string, string>
  exampleBonusAmount: number
  setAllocationTables: (tables: VariableAllocationTable[]) => void
  exampleSalary: number
  variableCompensation: number
  templateName: string
  setIsValid: (isValid: boolean) => void // New prop for setting validity
  addTargetIfNotExists: (newTarget: ExpandedTarget) => void
  theme: Theme
  currency: string
  currencyDecimals: number
}

interface ValidationResult {
  isValid: boolean
  message: string
  color?: string
  backgroundColor?: string
}

const TemplateComponents: FC<TemplateComponentsProps> = ({
  period,
  allocationTables,
  targets,
  organizationLabels,
  periodLabels,
  exampleBonusAmount,
  setAllocationTables,
  exampleSalary,
  variableCompensation,
  templateName,
  setIsValid, // New prop for setting validity
  addTargetIfNotExists,
  theme,
  currency,
  currencyDecimals,
}) => {
  const [editingTableIndex, setEditingTableIndex] = useState<number | null>(null)
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null) // New state for tracking the editing row index
  const [editingLabel, setEditingLabel] = useState<string>('')
  const [editingTableName, setEditingTableName] = useState<string>('') // New state for the editing table name
  const [orderedLabels, setOrderedLabels] = useState<string[]>([])
  const [labelToIdMap, setLabelToIdMap] = useState<Record<string, string>>({})

  const [validation, setValidation] = useState<ValidationResult>({ isValid: false, message: '' })

  const validateAllocations = (): ValidationResult => {
    let totalAllocation = 0
    let incompleteRows = 0

    if (allocationTables.length === 0) {
      return { isValid: false, message: 'Status: Please add at least one Section' }
    }
    // Check if any table has no rows
    for (const table of allocationTables) {
      if (table.rows.length === 0) {
        return { isValid: false, message: 'Status: Please add a row to a section' }
      }
    }

    allocationTables.forEach((table) => {
      table.rows.forEach((row) => {
        if (row.allocationPercentage) {
          totalAllocation += row.allocationPercentage
        }
        console.log('AllocationPercentage', row.allocationPercentage)
        if (
          !row.label ||
          !row.periodId ||
          (!row.allocationPercentage && row.rowType !== RowType.DISPLAY_ROW) ||
          row.formulaOptions.length === 0
        ) {
          incompleteRows++
        }
      })
    })

    // Use four decimal precision for total allocation
    const roundedTotal = Math.round(totalAllocation * 10000) / 10000 // e.g. 0.9999, 1.0000
    const isTotalValid = roundedTotal === 1.0 // must be exactly 1.0000

    console.log('roundedTotal', roundedTotal)
    console.log('isTotalValid', isTotalValid)

    const roundedTotalPercentage = (roundedTotal * 100).toFixed(2)

    if (!isTotalValid) {
      return {
        isValid: false,
        message: `Status: Total allocation must be exactly 100%. Current total: ${roundedTotalPercentage}%`,
      }
    }

    if (incompleteRows > 0) {
      return { isValid: false, message: `${incompleteRows} row(s) are incomplete` }
    }

    return { isValid: true, message: 'All allocations are valid' }
  }

  useEffect(() => {
    if (period) {
      const ordered = getOrderedLabels(period)
      setOrderedLabels(ordered)
      setLabelToIdMap(createLabelToIdMap(period))
    }
  }, [period, allocationTables])

  useEffect(() => {
    const validationResult = validateAllocations()
    console.log('validationResult', validationResult)
    setValidation(validationResult)
    setIsValid(validationResult.isValid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationTables])

  const addTable = () => {
    const sectionCount = allocationTables.length
    const newTable: VariableAllocationTable = {
      label: `Section #${sectionCount + 1}`,
      rows: [],
    }
    setAllocationTables([...allocationTables, newTable])
  }

  const removeTable = (tableIndex: number) => {
    const updatedTables = allocationTables.filter((_, index) => index !== tableIndex)
    setAllocationTables(updatedTables)
  }

  const addRow = (tableIndex: number) => {
    const newRow: VariableAllocationRow = {
      label: 'New Row',
      rowType: RowType.CALCULATION_ROW,
      restrictedVisibility: false,
      allocationPercentage: 0.2,
      minAllocationPercentage: 0.1,
      maxAllocationPercentage: 0.3,
      defaultPayout: 1.5,
      minPayout: 1.0,
      maxPayout: 2.0,
      formulaRequirements: [],
      formulaOptions: [],
      achievementMultiplier: 'a**1.00',
      periodId: '',
    }
    const updatedTables = allocationTables.map((table, index) => {
      if (index === tableIndex) {
        return { ...table, rows: [...table.rows, newRow] }
      }
      return table
    })
    setAllocationTables(updatedTables)
  }

  const removeRow = (tableIndex: number, rowIndex: number) => {
    const updatedTables = allocationTables.map((table, index) => {
      if (index === tableIndex) {
        const updatedRows = table.rows.filter((_, rIndex) => rIndex !== rowIndex)
        return { ...table, rows: updatedRows }
      }
      return table
    })
    setAllocationTables(updatedTables)
  }

  const updateTableRows = (
    tableIndex: number,
    rowIndex: number | null,
    updateCallback: (row: VariableAllocationRow) => VariableAllocationRow
  ) => {
    const updatedTables = allocationTables.map((table, tIndex) => {
      if (tIndex === tableIndex) {
        const updatedRows = table.rows.map((row, rIndex) => {
          if (rowIndex === null || rIndex === rowIndex) {
            return updateCallback(row)
          }
          return row
        })
        return { ...table, rows: updatedRows }
      }
      return table
    })
    setAllocationTables(updatedTables)
  }

  const handleEditClick = (tableIndex: number, rowIndex: number, label: string) => {
    setEditingTableIndex(tableIndex)
    setEditingRowIndex(rowIndex)
    setEditingLabel(label)
    setEditingTableName('') // Reset table name editing when editing row label
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editingTableIndex !== null && editingRowIndex !== null) {
      updateTableRows(editingTableIndex, editingRowIndex, (row) => ({
        ...row,
        label: e.target.value,
      }))
      setEditingLabel(e.target.value)
    }
  }

  const handleRowLabelChange = () => {
    if (editingTableIndex !== null && editingRowIndex !== null) {
      updateTableRows(editingTableIndex, editingRowIndex, (row) => ({
        ...row,
        label: editingLabel,
      }))
      setEditingTableIndex(null)
      setEditingRowIndex(null) // Reset the editing row index
      setEditingLabel('')
    }
  }

  const handleEditTableClick = (tableIndex: number, tableName: string) => {
    setEditingTableIndex(tableIndex)
    setEditingRowIndex(null) // Reset row editing when editing table name
    setEditingTableName(tableName)
  }

  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTableName(e.target.value)
  }

  const handleTableNameSave = () => {
    if (editingTableIndex !== null) {
      // Create a new array with all tables
      const updatedTables = allocationTables.map((table, index) => {
        // If this is the table we're editing, update its label
        if (index === editingTableIndex) {
          return { ...table, label: editingTableName }
        }
        // Otherwise, return the table as is
        return table
      })
      // Update the state with the new tables array
      setAllocationTables(updatedTables)
      // Reset editing state
      setEditingTableIndex(null)
      setEditingRowIndex(null)
      setEditingTableName('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingRowIndex !== null && editingTableIndex !== null) {
        handleRowLabelChange() // Existing functionality for saving row label edits
      } else if (editingTableIndex !== null) {
        handleTableNameSave() // Save table name edits when editing table name
      }
    }
  }

  const handlePeriodChange = (event: string, tableIndex: number, rowIndex: number) => {
    // Update the tables with the new period ID
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      periodId: event,
    }))
  }

  const handleRestrictedVisibilityChange = (tableIndex: number, rowIndex: number, checked: boolean) => {
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      restrictedVisibility: checked,
    }))
  }

  const handleRowTypeChange = (event: SelectChangeEvent<string>, tableIndex: number, rowIndex: number) => {
    const newType = event.target.value as RowType
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      rowType: newType,
      allocationPercentage: newType == RowType.DISPLAY_ROW ? 0 : row.allocationPercentage,
      defaultPayout: newType == RowType.DISPLAY_ROW ? 0 : row.defaultPayout,
      minPayout: row.minPayout,
      maxPayout: row.maxPayout,
      achievementMultiplier: newType !== RowType.CALCULATION_ROW ? 'a**1.00' : row.achievementMultiplier,
    }))
  }

  const handleFormulaChange = (
    tableIndex: number,
    rowIndex: number,
    newFormulaRequirements: string[],
    newFormulaOptions: string[]
  ) => {
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      formulaRequirements: newFormulaRequirements,
      formulaOptions: newFormulaOptions,
    }))
  }

  const handleRowUpdate = (
    tableIndex: number,
    rowIndex: number,
    newFormulaRequirements: string[],
    newFormulaOptions: string[],
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number,
    newAccelerator: string
  ) => {
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      formulaRequirements: newFormulaRequirements,
      formulaOptions: newFormulaOptions,
      allocationPercentage,
      minAllocationPercentage,
      maxAllocationPercentage,
      defaultPayout: defaultPayoutPercent,
      minPayout: minPayoutPercent,
      maxPayout: maxPayoutPercent,
      achievementMultiplier: newAccelerator,
    }))
  }

  const handleUpdateAllocations = (
    tableIndex: number,
    rowIndex: number,
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number
  ) => {
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      allocationPercentage,
      minAllocationPercentage,
      maxAllocationPercentage,
      defaultPayout: defaultPayoutPercent,
      minPayout: minPayoutPercent,
      maxPayout: maxPayoutPercent,
    }))
  }

  const handleEditAchievementMultiplier = (tableIndex: number, rowIndex: number, newMultiplier: string) => {
    updateTableRows(tableIndex, rowIndex, (row) => ({
      ...row,
      achievementMultiplier: newMultiplier,
    }))
  }

  console.log('[TemplateComponents] targets prop:', targets)
  console.log('[TemplateComponents] targets length:', targets?.length || 0)

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <AllocationTableDisplay
            templateName={templateName}
            allocationTables={allocationTables}
            orderedLabels={orderedLabels}
            labelToIdMap={labelToIdMap}
            editingTableIndex={editingTableIndex}
            editingRowIndex={editingRowIndex}
            editingLabel={editingLabel}
            editingTableName={editingTableName}
            handleEditClick={handleEditClick}
            handleLabelChange={handleLabelChange}
            handleLabelSave={handleRowLabelChange}
            handleEditTableClick={handleEditTableClick}
            handleTableNameChange={handleTableNameChange}
            handleTableNameSave={handleTableNameSave}
            handleKeyPress={handleKeyPress}
            handlePeriodChange={handlePeriodChange}
            handleRestrictedVisibilityChange={handleRestrictedVisibilityChange}
            handleRowTypeChange={handleRowTypeChange}
            handleEditAchievementMultiplier={handleEditAchievementMultiplier}
            handleFormulaChange={handleFormulaChange}
            handleUpdateAllocations={handleUpdateAllocations}
            handleRowUpdate={handleRowUpdate}
            addRow={addRow}
            removeRow={removeRow}
            addTable={addTable}
            period={period!}
            removeTable={removeTable}
            targets={targets}
            organizationLabels={organizationLabels}
            periodLabels={periodLabels}
            rootPeriod={period?.label || ''}
            exampleBonusAmount={exampleBonusAmount}
            setEditingIndices={(tableIndex: number | null, rowIndex: number | null) => {
              setEditingTableIndex(tableIndex)
              setEditingRowIndex(rowIndex)
            }}
            exampleSalary={exampleSalary}
            variableCompensation={variableCompensation}
            addTargetIfNotExists={addTargetIfNotExists}
            theme={theme}
            currency={currency}
            currencyDecimals={currencyDecimals}
          />
        </Grid>
      </Grid>
      {!validation.isValid && (
        <Typography color={validation.color} sx={{ mt: 2 }}>
          {validation.message}
        </Typography>
      )}
    </>
  )
}

export default TemplateComponents
